import React from 'react';
import { Avatar, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { LocalAudioTrack, LocalVideoTrack, Participant, RemoteAudioTrack, RemoteVideoTrack } from 'twilio-video';
import { useParticipantIsReconnecting, useTrack, usePublications, useIsTrackSwitchedOff, useVideoContext } from '../hooks';
import { AudioLevelIndicator } from './audio-level-indicator';
import { getParticipantNameAndInitials } from './helpers';

const useStyles = makeStyles()((theme) => ({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    height: 0,
    overflow: 'hidden',
    '& video': {
      filter: 'none',
      objectFit: 'contain !important' as 'contain',
    },
    paddingTop: `calc(${(9 / 16) * 100}% - 2px)`,
  },
  innerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  infoContainer: {
    position: 'absolute',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    width: '100%',
    background: 'transparent',
    top: 0,
  },
  avatarContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'black',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    [theme.breakpoints.down('md')]: {
      '& svg': {
        transform: 'scale(0.7)',
      },
    },
  },
  reconnectingContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(40, 42, 43, 0.75)',
    zIndex: 1,
  },
  screenShareIconContainer: {
    background: 'rgba(0, 0, 0, 0.5)',
    padding: '0.18em 0.3em',
    marginRight: '0.3em',
    display: 'flex',
    '& path': {
      fill: 'white',
    },
  },
  identity: {
    color: 'white',
    margin: 0,
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    padding: '0.25rem 0.25rem 0 0.5rem',

    [theme.breakpoints.up('sm')]: {
      padding: '0 0.25rem 0 0',
      background: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'unset',
      flexDirection: 'unset',
    },

    [theme.breakpoints.up('md')]: {
      padding: '0.18rem 0.25rem 0 0.1rem',
    }
  },
  infoRowBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',

    [theme.breakpoints.up('sm')]: {
      right: 'unset',
      width: 'unset',
    }
  },
  typography: {
    color: 'white',
    fontSize: '14px',
    lineHeight: 1,

    [theme.breakpoints.up('sm')]: {
      fontSize: '12px'
    },

    [theme.breakpoints.up('md')]: {
      fontSize: 'unset',
    },
  },
  hideParticipant: {
    display: 'none',
  },
  cursorPointer: {
    cursor: 'pointer',
  },
  avatarSize: {
    width: '2.5rem',
    height: '2.5rem',
    background: '#A568F3',
    fontFamily: 'Futura PT',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '1rem'
  }
}));

interface ParticipantInfoProps {
  participant: Participant;
  children: React.ReactNode;
  onClick?: () => void;
  isSelected?: boolean;
  isLocalParticipant?: boolean;
  hideParticipant?: boolean;
}

export function ParticipantInfo({
  participant,
  onClick,
  children,
  isLocalParticipant,
  hideParticipant,
}: ParticipantInfoProps) {
  const { patient, provider } = useVideoContext();
  const { name, initials } = getParticipantNameAndInitials(patient, provider, participant);
  const publications = usePublications(participant);
  const audioPublication = publications.find(p => p.kind === 'audio');
  const videoPublication = publications.find(p => !p.trackName.includes('screen') && p.kind === 'video');
  const isVideoEnabled = Boolean(videoPublication);
  const videoTrack = useTrack(videoPublication);
  const isVideoSwitchedOff = useIsTrackSwitchedOff(videoTrack as LocalVideoTrack | RemoteVideoTrack);
  const audioTrack = useTrack(audioPublication) as LocalAudioTrack | RemoteAudioTrack | undefined;
  const isParticipantReconnecting = useParticipantIsReconnecting(participant);
  const { classes, cx } = useStyles();

  return (
    (
      <div
        className={cx(classes.container, {
          [classes.hideParticipant]: hideParticipant,
          [classes.cursorPointer]: Boolean(onClick),
        })}
        onClick={onClick}
        data-cy-participant={participant?.identity}
      >
        <div className={classes.infoContainer}>
          {/* <NetworkQualityLevel participant={participant} /> */}
          <div className={classes.infoRowBottom}>
            <span className={classes.identity}>
              <AudioLevelIndicator audioTrack={audioTrack} />
              <Typography variant="body1" className={classes.typography} component="span">
                {!isLocalParticipant && name}
                {isLocalParticipant && 'You'}
              </Typography>
            </span>
          </div>
          {/* <div>{isSelected && <PinIcon />}</div> */}
        </div>
        <div className={classes.innerContainer}>
          {(!isVideoEnabled || isVideoSwitchedOff) && (
            <div className={classes.avatarContainer}>
              <Avatar className={classes.avatarSize} alt={name}>{initials}</Avatar>
            </div>
          )}
          {isParticipantReconnecting && (
            <div className={classes.reconnectingContainer}>
              <Typography variant="body1" className={classes.typography}>
                Reconnecting...
              </Typography>
            </div>
          )}
          {children}
        </div>
      </div>
    )
  );
}
